
import React from 'react';
import { useStore } from '../../hooks/useStore.ts';
import { api } from '../../services/api.ts';
import { Project, AiReport } from '../../types.ts';
import { Printer, Download, Sparkles } from 'lucide-react';

export const ReportsSection: React.FC<{ project: Project }> = ({ project }) => {
  const reports = useStore(() => api.reports.list(r => r.projectId === project.id), [project.id], 'reports') as AiReport[] | undefined;

  const handlePrint = () => {
      window.print();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-16 animate-in fade-in duration-700">
        <div className="bg-black p-12 rounded-[3.5rem] text-white flex justify-between items-center shadow-2xl border border-zinc-800 print:hidden">
           <div className="max-w-lg">
             <div className="flex items-center gap-3 mb-4">
                <Sparkles className="text-amber-400" size={24}/>
                <h3 className="text-3xl font-black tracking-tighter uppercase">Executive Synthesis</h3>
             </div>
             <p className="text-zinc-400 text-lg font-medium">Distilling operational builds into executive-grade margin intelligence.</p>
           </div>
           <button onClick={handlePrint} className="p-5 bg-white text-black rounded-2xl hover:scale-110 transition-all shadow-xl">
               <Printer size={24}/>
           </button>
        </div>

        <div className="space-y-12 print:space-y-12">
          {reports?.sort((a,b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()).map(r => (
              <div key={r.id} className="bg-white border border-zinc-200 rounded-[3.5rem] p-12 shadow-sm group hover:shadow-2xl transition-all duration-700 print:shadow-none print:border-zinc-100 print:rounded-none print:p-0 print:border-b print:pb-12">
                  <div className="flex justify-between mb-10 items-start">
                     <div>
                       <h4 className="text-3xl font-black text-black tracking-tighter uppercase leading-none mb-4">{r.title}</h4>
                       <div className="flex items-center gap-4">
                          <span className={`px-3 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest ${
                              r.statusColor === 'Green' ? 'bg-emerald-100 text-emerald-600' :
                              r.statusColor === 'Amber' ? 'bg-amber-100 text-amber-600' : 'bg-rose-100 text-rose-600'
                          }`}>{r.statusColor} Index</span>
                          <p className="text-[10px] text-zinc-400 font-black uppercase tracking-[0.2em]">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : 'N/A'} • {r.reportType}</p>
                       </div>
                     </div>
                  </div>
                  <div className="text-zinc-700 text-lg leading-relaxed whitespace-pre-wrap font-normal border-l-4 border-zinc-100 pl-8 mb-10 print:text-sm">{r.summaryText}</div>
                  
                  {r.recommendations && r.recommendations.length > 0 && (
                      <div className="space-y-4">
                          <h5 className="text-[10px] font-black uppercase text-zinc-400 tracking-widest">Operational Directives</h5>
                          <ul className="grid md:grid-cols-2 gap-4">
                              {r.recommendations.map((rec, i) => (
                                  <li key={i} className="bg-zinc-50 p-6 rounded-2xl border border-zinc-100 text-sm font-normal text-black flex gap-4 print:bg-white print:p-2">
                                      <span className="text-zinc-400 font-black">0{i+1}</span>
                                      <span className="font-bold">{rec}</span>
                                  </li>
                              ))}
                          </ul>
                      </div>
                  )}
              </div>
          ))}
          {(!reports || reports.length === 0) && (
            <div className="p-20 text-center border-4 border-dashed border-zinc-100 rounded-[3rem]">
              <p className="text-zinc-300 font-black uppercase tracking-widest text-xs">No executive briefs synthesized.</p>
            </div>
          )}
        </div>
        
        <div className="hidden print:flex justify-between items-center border-t border-zinc-100 pt-8 mt-20">
            <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 tracking-widest">LeanControls Operational Intelligence Summary</div>
            <div className="text-[10px] font-black text-black">INTERNAL USE ONLY</div>
        </div>
    </div>
  );
};
